// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

export default function RequestPasswordReset() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const errorId = error ? 'reset-request-error' : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await api.post('/auth/member/request-reset', {
        email: email.trim()
      });
      const responseMessage = response?.data?.message as string | undefined;
      setMessage(responseMessage || 'If this email is registered, you will receive a password reset link.');
    } catch (err: unknown) {
      let errorMessage = 'Unable to process reset request. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string }; status?: number } };
        if (axiosError.response?.status === 429) {
          errorMessage = 'Too many reset requests. Please try again later.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
        <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">Reset your password</h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          Enter your member email and we will send you a reset link.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} aria-describedby={errorId} noValidate>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          {error ? (
            <p id={errorId} role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : message ? (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</p>
          ) : (
            <p className="text-xs text-slate-500">We will only send a link if the email exists.</p>
          )}
          <button
            type="submit"
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-brand-600 hover:text-brand-700" aria-label="Back to sign in">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
